import { useTranslation } from "react-i18next";
import { clx } from "../../../../helpers/components";
import { Reveal } from "../../../shared/Reveal";
import style from "./style.module.scss";

// Lien du bouton « Envoyer » — À FOURNIR (placeholder).
const SUBMIT_URL = "https://tally.so/r/A7RrEl";

// Étapes (numéro + clés i18n). Positions gérées en CSS (step1/2/3) d'après le
// JSON Figma (groupe « Process » 1209.5×621).
const STEPS = [
    { num: "1", title: "step1Title", desc: "step1Desc" },
    { num: "2", title: "step2Title", desc: "step2Desc" },
    { num: "3", title: "step3Title", desc: "step3Desc" },
];

// Icône 64×64 (placeholder Figma : carte blanche + carré gris).
function StepIcon() {
    return (
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="64" height="64" rx="20" fill="white" />
            <rect x="20" y="20" width="23" height="23" rx="10" fill="#C4C4C4" />
        </svg>
    );
}

/**
 * Section « Comment ça fonctionne ? » (Figma node 10030:7020).
 * Positionnement fidèle au JSON : canvas absolu (échelle 1209.5×621, unités cqw)
 * en desktop ; repli empilé en mobile.
 */
export function ProcessSteps({ base = "partners" } = {}) {
    const { t: t0 } = useTranslation("translations");
    const t = (key, opts) =>
        t0(typeof key === "string" ? key.replace(/^partners\./, `${base}.`) : key, opts);

    const openUrl = (url) => {
        if (!url || url === "#") return;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <Reveal className={style.section}>
            <div className={clx("container", style.wrapper)}>
                <div className={style.stage}>
                    {/* Ellipse décorative (derrière l'étape 3) */}
                    <span className={style.ellipse} aria-hidden="true" />

                    {/* Courbe (Vector 335) — coordonnées Process exactes */}
                    <svg
                        className={style.curve}
                        viewBox="0 0 1209.5 621"
                        fill="none"
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            className={style.curvePath}
                            d="M0 369 C48 404 159.8 470 223 454 C302 434 321.5 331 443.5 307 C565.5 283 655 366.5 749.5 246 C844 125.5 890 46.5 1017 63.5"
                            pathLength="1"
                            stroke="#008080"
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>

                    {/* En-tête */}
                    <span className={style.eyebrow}>{t("partners.process.eyebrow")}</span>
                    <h2 className={style.title}>{t("partners.process.title")}</h2>
                    <p className={style.subtitle}>{t("partners.process.subtitle")}</p>

                    {/* Bouton Envoyer */}
                    <button
                        type="button"
                        className={style.button}
                        onClick={() => openUrl(SUBMIT_URL)}
                    >
                        <span>{t("partners.process.btn")}</span>
                        <svg
                            className={style.arrow}
                            width="20"
                            height="12"
                            viewBox="0 0 20 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <g clipPath="url(#processArrow)">
                                <path
                                    d="M1.48159 5.10577C0.970212 5.10577 0.555664 5.52032 0.555664 6.0317C0.555664 6.54307 0.970212 6.95762 1.48159 6.95762V5.10577ZM13.7843 6.95762C14.2957 6.95762 14.7103 6.54307 14.7103 6.0317C14.7103 5.52032 14.2957 5.10577 13.7843 5.10577V6.95762ZM1.48159 6.95762H13.7843V5.10577H1.48159V6.95762ZM17.4894 6.27286L15.2003 8.03501L16.3299 9.50241L18.619 7.74028L17.4894 6.27286ZM14.7103 7.79384V4.26955H12.8584V7.79384H14.7103ZM15.2003 4.02838L17.4894 5.79052L18.619 4.32311L16.3299 2.56096L15.2003 4.02838ZM14.7103 4.26955C14.7103 4.01698 15.0001 3.87432 15.2003 4.02838L16.3299 2.56096C14.912 1.4695 12.8584 2.48024 12.8584 4.26955H14.7103ZM15.2003 8.03501C15.0001 8.18907 14.7103 8.04641 14.7103 7.79384H12.8584C12.8584 9.58315 14.912 10.5939 16.3299 9.50241L15.2003 8.03501ZM18.619 7.74028C19.7402 6.87715 19.7402 5.18624 18.619 4.32311L17.4894 5.79052C17.6476 5.91236 17.6476 6.15103 17.4894 6.27286L18.619 7.74028Z"
                                    fill="white"
                                />
                            </g>
                            <defs>
                                <clipPath id="processArrow">
                                    <rect width="20" height="12" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </button>

                    {/* Étapes */}
                    {STEPS.map((s, i) => (
                        <div key={s.num} className={clx(style.step, style[`step${i + 1}`])}>
                            <span
                                className={clx(style.stepNum, style[`num${i + 1}`])}
                                aria-hidden="true"
                            >
                                {s.num}
                            </span>
                            <span className={style.stepIcon} aria-hidden="true">
                                <StepIcon />
                            </span>
                            <div className={style.stepBody}>
                                <h3 className={style.stepTitle}>
                                    {t(`partners.process.${s.title}`)}
                                </h3>
                                <p className={style.stepDesc}>
                                    {t(`partners.process.${s.desc}`)}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Reveal>
    );
}
